import { basic } from '@shared/cove-data/monitoring';
import {cn, Modal} from '@shared/cove-ui';
import { UICoreColor } from '@shared/react-ui-core';
import { useEffect, useState } from 'react';

const checkIcon =
  'https://www.datocms-assets.com/10154/1657618272-check-icon-green-new.svg';
const uncheckedIcon =
  'https://www.datocms-assets.com/10154/1657618327-x-icon-grey-new.svg';

/**
 *
 * @param {boolean} check
 * @returns
 */
const checkXMark = (check: boolean) => {
  return (
    <img
      src={check ? checkIcon : uncheckedIcon}
      alt={check ? 'Checked' : 'Unchecked'}
      width={check ? 15 : 13}
      height={13}
    />
  );
};

/**
 *
 * @param {boolean | string | object} value
 */
const getValue = (value: string | boolean | Record<string, any>) => {
  /**
   * @type {import('react').ReactNode}
   */
  let actualValue;

  switch (typeof value) {
    case 'boolean':
      actualValue = checkXMark(value);
      break;
    case 'string':
      actualValue = (
        <span className="mb-auto block text-center text-xs leading-[18px] text-cove-navy-blue sm:text-base">
          {value}
        </span>
      );
      break;
    case 'object':
      actualValue = (
        <>
          <span className="block text-center text-xs leading-[18px] sm:hidden">
            {value.mobile}
          </span>
          <span className="hidden sm:block">{value.tablet}</span>
        </>
      );
      break;
    default:
      actualValue = <span>{value}</span>;
  }

  return (
    <div className="grid h-full w-[calc(100%-16px)] place-content-center items-center justify-center border-t border-cove-baby-dove sm:w-[calc(100%-46px)] ">
      {actualValue}
    </div>
  );
};

/**
 *
 * @param {boolean | string | object} value
 * @param {boolean} isLastRow
 * @returns
 */
const checkRenderer = (
  value: boolean | string | Record<string, unknown>,
  isLastRow: boolean,
) => {
  return (
    <div
      className={cn(
        'mr-cove-5 flex h-full w-full max-w-cove-75 items-center justify-center border-x border-cove-navy-blue bg-cove-white xs:w-cove-150 xs:max-w-cove-150 sm:mr-cove-25',
        isLastRow && 'border-b border-cove-navy-blue',
      )}
    >
      {getValue(value)}
    </div>
  );
};

interface IExperienceCoveProps {
  title?: string | null | undefined;
  background?: UICoreColor;
  separator?: boolean | null | undefined;
  className?: string;
}

const ExperienceCove: React.FC<IExperienceCoveProps> = ({
  title = 'See How Cove Compares',
  background = 'light',
  separator,
  className,
  ...props
}) => {
  const [popup, setPopup] = useState(-1);
  const [hiddenIndex, setHiddenIndex] = useState(2);
  const [windowWidth, setWindowWidth] = useState(570);
  const isMobile = windowWidth < 577;
  const isTooSkinny = windowWidth < 1100;
  const chartValues = [
    {
      name: 'Wireless equipment, no drilling',
      checked: [true, false],
      popupText:
        'Installing your system is a breeze with our wireless sensors. Just peel the adhesive backing and place them where you want them.',
    },
    {
      name: 'Low upfront and monthly costs ',
      checked: [true, false],
      popupText: `Our equipment costs less than half of what most traditional providers charge, and our professional monitoring fee is as low as $${basic.price}/month.`,
    },
    {
      name: 'Professional Monitoring',
      checked: [true, true],
      popupText:
        'With three award-winning monitoring stations manned by trained professionals, your home will be secure around the clock for half the cost of traditional providers.',
    },
    {
      name: '$5/Month Equipment Reward*',
      checked: [true, false],
      popupText:
        'If you’re on our Plus plan, you’ll receive $5/month of free credit to use toward adding more equipment to your system.',
    },
    {
      name: 'Lifetime equipment warranty*',
      checked: [true, false],
      popupText: `On our Plus plan, you’ll receive a lifetime equipment warranty, so if anything ever happens to your system, you’ll be able to replace it quickly and easily. (Cameras not included)`,
    },
    {
      name: 'No Contracts',
      checked: [true, false],
      popupText: `We’ll never require you to sign a contract. If at any point you decide Cove isn’t for you, just call our customer support team to cancel your service.`,
    },
    {
      name: `Average installation time`,
      checked: [
        '27 min',
        { mobile: '3 hours**', tablet: 'Professional required' },
      ],
      popupText:
        'You shouldn’t have to spend hours setting up your system. Just plug in your panel and follow the instructions. Professional installation available.',
    },
    {
      name: `Can move with you`,
      checked: [true, false],
      popupText:
        'Moving with Cove is easy. Just take down your sensors, turn off your panel, and let us know your new address.',
    },
    {
      name: '24/7 Customer Support',
      checked: [true, false],
      popupText:
        'Cove is the only self-installed security company that provides 24/7 customer support. Whether you have billing questions or need help installing a new sensor, we’re here.',
    },
  ];

  useEffect(() => {
    const handleResize = () => {
      if (typeof window !== 'undefined') {
        setWindowWidth(window.innerWidth);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });

  return (
    <div className={cn("mx-auto max-w-full bg-cove-white", className)} {...props}>
      <div className="mx-auto max-w-xxl bg-cove-white px-cove-25 py-cove-50 md:p-cove-50 lg:px-cove-100 lg:py-cove-75">
        {separator && (
          <div className="mb-cove-50 w-full border-b-2 border-cove-baby-dove sm:mb-cove-100"></div>
        )}
        <h2 className="mb-cove-10 text-left text-h2 font-bold text-cove-navy-blue sm:mb-cove-35 sm:text-center">
          {title}
        </h2>
        <div>
          <div className="relative mx-auto flex h-cove-137 max-w-lg items-center sm:h-cove-87">
            {/* eslint-disable-next-line jsx-a11y/heading-has-content */}
            <h3 className="relative mr-cove-5 flex h-full w-[77%] flex-col justify-center sm:mr-cove-25"></h3>
            <div className="mr-cove-5 flex h-full w-full max-w-cove-75 items-center justify-center border-x border-t border-cove-navy-blue bg-cove-white xs:w-cove-150 xs:max-w-cove-150 sm:mr-cove-25">
              <img
                src="https://www.datocms-assets.com/10154/1678910839-cove-logo-teal-new.png"
                width={78}
                height={24}
                className="max-w-cove-75 xs:max-w-cove-87"
                alt="Cove Logo"
              />
            </div>
            {(!isMobile || (isMobile && hiddenIndex !== 1)) && (
              <div className="mr-cove-5 flex h-full w-full max-w-cove-75 items-center justify-center border-x border-t border-cove-navy-blue bg-cove-white text-center text-xs font-medium text-cove-navy-blue xs:w-cove-150 xs:max-w-cove-150 xs:text-base sm:mr-cove-25">
                <div id="footnotes-ref">
                  Traditional Security
                  <a href="#compareFootnotes" aria-label="Footnote">
                    †
                  </a>
                </div>
              </div>
            )}
          </div>
          {chartValues.map((row, index) => (
            <div
              className="relative mx-auto flex h-cove-137 max-w-lg items-center sm:h-cove-87"
              key={row.name}
            >
              {
                <Modal open={index === popup} onOpenChange={(open) => setPopup(!open ? -1 : index)}>
                  <Modal.Trigger>
                    <>
                      <div className="fixed bottom-0 left-0 right-0 top-0 z-[1000] hidden bg-cove-dark-grey" />
                      <div className="relative mr-cove-5 flex h-full w-[77%] flex-col items-start justify-center border-t border-cove-baby-dove sm:mr-cove-25">
                        <h3
                          className="mb-0 cursor-pointer text-base font-medium text-cove-navy-blue sm:text-h4"
                          dangerouslySetInnerHTML={{ __html: row.name }}
                        />
                        <button
                          className="mt-cove-5 cursor-pointer border-none bg-transparent p-0 text-sm font-light text-cove-blue underline"
                          onClick={() => setPopup(index)}
                        >
                          Details
                        </button>
                      </div>
                    </>
                  </Modal.Trigger>
                  <Modal.Content className='fixed left-1/2 top-1/2 flex min-h-cove-290 w-[80%] items-center justify-center rounded-sm border-0 bg-cove-baby-dove p-cove-25'>
                    <p className="m-0 max-w-xs text-cove-dark-grey">
                      {row.popupText}
                    </p>
                  </Modal.Content>
                </Modal>
              }
              {checkRenderer(row.checked[0], index === chartValues.length - 1)}
              {checkRenderer(row.checked[1], index === chartValues.length - 1)}
            </div>
          ))}
        </div>
        <span
          className="mx-auto mt-cove-35 block max-w-md text-center text-[12px]"
          style={{ position: 'relative' }}
        >
          <span
            id="compareFootnotes"
            style={{ position: 'absolute', top: '-100px' }}
          />
          †Individual systems and features may vary. {isTooSkinny && <br />}
          Cove encourages consumers to conduct their own independent research.{' '}
          {isTooSkinny && <br />}* Standard with Cove Plus Monitoring{' '}
        </span>
        <a className="skip" href="#footnotes-ref" aria-label="Back to content">
          ↵
        </a>
      </div>
    </div>
  );
};

export default ExperienceCove;
